import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleCategorySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const link = e.target.value;
    if (link) {
      window.open(link, '_blank', 'noopener=yes,noreferrer=yes');
      e.target.value = '';
    }
  };

  return (
    <section className="relative w-full min-h-[580px] md:min-h-[680px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20artistic%20flat%20lay%20composition%20of%20modern%20lifestyle%20products%20including%20a%20pair%20of%20designer%20sneakers%20a%20leather%20backpack%20a%20minimalist%20analog%20watch%20and%20a%20ceramic%20coffee%20cup%2C%20arranged%20on%20a%20warm%20sand%20colored%20linen%20fabric%2C%20soft%20diffused%20daylight%20from%20a%20large%20window%20with%20sheer%20curtains%2C%20subtle%20shadows%2C%20editorial%20magazine%20style%20photography%2C%20neutral%20earthy%20tones%2C%20cream%20beige%20taupe%20and%20warm%20grey%2C%20aspirational%20lifestyle%20aesthetic%2C%20plenty%20of%20negative%20space%20for%20text%20overlay&width=1800&height=900&seq=hero-bg-kakobuy-v3&orientation=landscape"
          alt="Kakobuy Spreadsheet Finds"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-background-50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-6 md:px-10">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-20">
            {/* Left: Text */}
            <div className="lg:w-[55%] pt-20 md:pt-24">
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight">
                <span className="block">{t('hero.title.line1')}</span>
                <span className="block text-3xl md:text-4xl lg:text-5xl font-medium mt-2 text-background-50/90">
                  {t('hero.title.line2')}
                </span>
                <span className="block text-2xl md:text-3xl lg:text-4xl font-normal mt-2 text-accent-300">
                  {t('hero.title.line3')}
                </span>
              </h1>
              <p className="mt-6 text-sm md:text-base text-background-50/80 max-w-lg leading-relaxed">
                {t('hero.subtitle')}
              </p>

              {/* Dropdown + Buttons */}
              <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                {/* Category Dropdown */}
                <div className="relative">
                  <select
                    onChange={handleCategorySelect}
                    defaultValue=""
                    className="appearance-none bg-background-50/90 backdrop-blur-sm text-foreground-900 text-sm font-medium pl-4 pr-10 py-3 rounded-full border border-background-200/50 outline-none cursor-pointer hover:bg-background-50 transition-colors min-w-[200px]"
                  >
                    <option value="" disabled>
                      {t('hero.dropdown.placeholder')}
                    </option>
                    {categories.map((cat) => (
                      <option key={cat.name} value={cat.link}>
                        {cat.namePl}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500 pointer-events-none text-sm"></i>
                </div>

                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm font-semibold px-6 py-3 rounded-full border-2 border-background-50 text-background-50 hover:bg-background-50 hover:text-foreground-900 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('hero.cta.primary')}
                </a>

                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm font-semibold px-6 py-3 rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('hero.cta.secondary')}
                </a>

                <button
                  onClick={() => navigate('/artykul/kakobuy-spreadsheet-kompletny-przewodnik')}
                  className="text-sm font-medium text-background-50/80 hover:text-background-50 underline underline-offset-4 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('article.read_more')} →
                </button>
              </div>

              {/* Trust badges */}
              <div className="mt-8 flex flex-wrap items-center gap-4 md:gap-6">
                <span className="flex items-center gap-1.5 text-xs text-background-50/70">
                  <i className="ri-check-line text-accent-400 text-sm"></i>
                  Regularnie aktualizowane
                </span>
                <span className="flex items-center gap-1.5 text-xs text-background-50/70">
                  <i className="ri-check-line text-accent-400 text-sm"></i>
                  Sprawdzone linki
                </span>
                <span className="flex items-center gap-1.5 text-xs text-background-50/70">
                  <i className="ri-check-line text-accent-400 text-sm"></i>
                  Szczegółowe poradniki
                </span>
              </div>
            </div>

            {/* Right: Empty space for breathing room */}
            <div className="lg:w-[45%] hidden lg:block"></div>
          </div>
        </div>
      </div>
    </section>
  );
}